// Define the custom block type locally to avoid circular dependencies
const CustomBlockType = {
  USERS_BLOCK: 'users_block' as const,
};
import { createCustomBlock } from '../../../utils/createCustomBlock';
import { t } from '../../../utils/I18nManager';
import { resolvePreviewColor } from '@core/utils';
import { BasicType } from '../../../constants';
import { IBlockData, RecursivePartial } from '../../../typings';
import { BasicBlock } from '../../../components/BasicBlock';
import { merge } from 'lodash';
import * as React from 'react';
import * as Handlebars from 'handlebars';
import { userLayouts } from '../../../data';

interface UserItem {
  id: string;
  name?: string;
  address?: string;
  licenceNumber?: string;
  phone?: string;
  profileImage?: string;
  title?: string; // Realtor or Broker
  email?: string;
}

interface IUsersBlockData {
  selectedUsers: UserItem[];
  maxCount: number;
}

interface IUsersBlockAttributes extends Record<string, any> {
  'background-color': string;
  'button-text-color': string;
  'button-color': string;
  'user-name-color': string;
  'user-title-color': string;
  'user-address-color': string;
  'user-phone-color': string;
  'user-licence-color': string;
  'title-font-size': string;
  'button-text': string;
  'show-name': boolean;
  'show-title': boolean;
  'show-address': boolean;
  'show-phone': boolean;
  'show-licence': boolean;
  layout: 'single' | 'double' | 'table';
  padding: string;
  title: string;
}

export type IUsersBlock = IBlockData<IUsersBlockAttributes, IUsersBlockData>;

// Helper function to prepare user data for handlebars templates
function prepareUser(user: UserItem) {
  const name = user.name || 'Unnamed Agent';
  const title = user.title || 'Realtor';
  const address = user.address || 'Address not provided';
  const phone = user.phone || 'Phone not provided';
  const licenceNumber = user.licenceNumber || 'License not provided';
  const email = user.email || 'email@example.com';

  const profileImage =
    user.profileImage || 'https://placehold.co/200x200/e1e1e1/666666?text=Profile';

  return {
    id: user.id,
    name,
    title,
    address,
    phone,
    licenceNumber,
    email,
    profileImage,
  };
}

// Helper function to pair users for double column layout
function pairUsers(users: any[]): any[][] {
  const pairs = [];
  for (let i = 0; i < users.length; i += 2) {
    const pair = [users[i]];
    if (i + 1 < users.length) {
      pair.push(users[i + 1]);
    }
    pairs.push(pair);
  }
  return pairs;
}

export const UsersBlock = createCustomBlock<IUsersBlock>({
  get name() {
    return t('Multiple');
  },
  type: CustomBlockType.USERS_BLOCK,
  validParentType: [
    BasicType.PAGE,
    BasicType.WRAPPER,
    BasicType.SECTION,
    BasicType.COLUMN,
  ],
  create: (payload?: RecursivePartial<IUsersBlock>) => {
    const defaultData: IUsersBlock = {
      type: CustomBlockType.USERS_BLOCK,
      data: {
        value: {
          selectedUsers: [],
          maxCount: 6,
        },
      },
      attributes: {
        'background-color': '#ffffff',
        'button-text-color': '#ffffff',
        'button-color': '#007bff',
        'user-name-color': '#000000',
        'user-title-color': '#0066cc',
        'user-address-color': '#666666',
        'user-phone-color': '#666666',
        'user-licence-color': '#666666',
        'title-font-size': '16px',
        'button-text': 'Contact Agent',
        'show-name': true,
        'show-title': true,
        'show-address': true,
        'show-phone': true,
        'show-licence': true,
        layout: 'double',
        padding: '20px',
        title: 'Multiple Users',
      },
      children: [],
    };
    return merge(defaultData, payload);
  },
  render: (params: {
    data: IUsersBlock;
    idx?: string | null;
    mode: 'testing' | 'production';
    context?: any;
    dataSource?: { [key: string]: any };
    children?: React.ReactNode;
    keepClassName?: boolean;
    renderPortal?: any;
  }) => {
    const { data } = params;
    const isTest = params.mode === 'testing';

    // Generate CSS classes for editor selectability [[memory:7441590]]
    let cssClasses = 'email-block';
    if (isTest && params.idx) {
      cssClasses = `email-block node-idx-${params.idx} node-type-${data.type}`;
    }
    const users = data.data.value.selectedUsers || [];
    const maxCount = data.data.value.maxCount || 6;
    const limitedUsers = users.slice(0, maxCount);
    const selectedLayout = data.attributes.layout || 'double';

    // If no users, show placeholder
    const displayUsers =
      limitedUsers.length > 0
        ? limitedUsers
        : [
            {
              id: 'placeholder',
              name: 'John Smith',
              address: '123 Real Estate Blvd, Suite 100, Anytown, CA 90210',
              licenceNumber: 'DRE# 12345678',
              phone: '(555) 123-4567',
              title: 'Senior Realtor',
              email: 'john.smith@realestate.com',
              profileImage:
                'https://placehold.co/200x200/e1e1e1/666666?text=Agent+Profile',
            },
          ];

    // Find the layout configuration
    const layoutConfig = userLayouts.find((layout: any) => layout.id === selectedLayout);
    if (!layoutConfig) {
      console.error(`Layout "${selectedLayout}" not found`);
      return (
        <BasicBlock
          params={params}
          tag='mj-text'
        >
          <div>Layout configuration not found</div>
        </BasicBlock>
      );
    }

    // Prepare handlebars data
    const processedUsers = displayUsers.map(prepareUser);

    // Resolve dynamic brand-colour merge tags to real hex in preview/testing mode;
    // in production the raw tag is returned unchanged for send-time resolution.
    const resolveColor = (key: string, fallback: string) =>
      resolvePreviewColor(data.attributes[key], params.mode, params.dataSource) ||
      fallback;

    // Prepare template context
    const templateContext = {
      cssClasses,
      blockTitle: data.attributes.title || 'Multiple Users',

      // Block attributes mapped to handlebars context
      backgroundColor: resolveColor('background-color', '#ffffff'),
      userNameColor: resolveColor('user-name-color', '#000000'),
      userTitleColor: resolveColor('user-title-color', '#0066cc'),
      userAddressColor: resolveColor('user-address-color', '#666666'),
      userPhoneColor: resolveColor('user-phone-color', '#666666'),
      userLicenceColor: resolveColor('user-licence-color', '#666666'),
      titleFontSize: data.attributes['title-font-size'] || '16px',
      buttonColor: resolveColor('button-color', '#007bff'),
      buttonTextColor: resolveColor('button-text-color', '#ffffff'),
      buttonText: data.attributes['button-text'] || 'Contact Agent',
      padding: data.attributes['padding'] || '20px',

      // Show/hide flags
      showName: data.attributes['show-name'] !== false,
      showTitle: data.attributes['show-title'] !== false,
      showAddress: data.attributes['show-address'] !== false,
      showPhone: data.attributes['show-phone'] !== false,
      showLicence: data.attributes['show-licence'] !== false,

      // Users data
      users: processedUsers,

      // For double column layout, we need paired users
      userPairs: selectedLayout === 'double' ? pairUsers(processedUsers) : [],
    };

    try {
      // Compile and render the handlebars template
      const template = Handlebars.compile(layoutConfig.data);
      const htmlContent = template(templateContext);

      // Create proper MJML structure as string (no dangerouslySetInnerHTML div wrapper)
      const mjmlContent = `
        <mj-section>
          <mj-column>
            <mj-raw>
              ${htmlContent}
            </mj-raw>
          </mj-column>
        </mj-section>
      `;

      return (
        <BasicBlock
          params={params}
          tag='mj-wrapper'
        >
          {mjmlContent}
        </BasicBlock>
      );
    } catch (error) {
      console.error('Error rendering handlebars template:', error);
      console.error('Layout config:', layoutConfig);
      console.error('Template context:', templateContext);
      return (
        <BasicBlock
          params={params}
          tag='mj-text'
        >
          <div>Error rendering template: {(error as Error).message}</div>
        </BasicBlock>
      );
    }
  },
});
